import React from 'react'
import resume from '../assets/content/resume.pdf'

const experiences = [
  {
    id: 1,
    period: 'Jun 2025 — Present',
    company: 'Apple, Inc.',
    role: 'Senior QA Engineer (Contract)',
    description:
      'Instrumentation QA across 12 LOBs using Splunk & Charles Proxy; zero regressions on all channel releases.',
    tags: ['Splunk', 'Charles Proxy', 'iOS', 'Analytics', 'QA'],
  },
  {
    id: 2,
    period: 'Sep 2024 — Jun 2025',
    company: 'Freelance',
    role: 'QA Automation Engineer',
    description:
      'Playwright-TypeScript suite (POM) for A/B testing campaigns; prevented regressions via visual snapshot testing.',
    tags: ['Playwright', 'TypeScript', 'Visual Testing', 'A/B Testing'],
  },
  {
    id: 3,
    period: 'Oct 2021 — Sep 2024',
    company: 'Scale Media, Inc.',
    role: 'Senior QA Engineer',
    description:
      'Cut smoke tests 65% via Python/Robot Framework + Jenkins CI/CD. $4M+ holiday sales impact; enforced DevSecOps with Snyk & SAST.',
    tags: ['Python', 'Robot Framework', 'Jenkins', 'Snyk', 'DevSecOps'],
  },
  {
    id: 4,
    period: 'Nov 2019 — Oct 2021',
    company: 'Internet Brands, Inc.',
    role: 'QA Engineer',
    description:
      'HTTPS migration of 5 apps, zero downtime. JMeter load tests (+5% perf); remediated SQL injection, XSS & GDPR/CCPA gaps.',
    tags: ['JMeter', 'Selenium', 'OWASP', 'GDPR', 'Security'],
  },
  {
    id: 5,
    period: 'Mar 2017 — Nov 2019',
    company: 'Internet Brands, Inc.',
    role: 'Associate QA Engineer',
    description:
      'Built foundational Python/Selenium automation suite, cutting manual testing 20%. Mentored 4 engineers; halved onboarding time.',
    tags: ['Python', 'Selenium', 'Robot Framework', 'TestRail', 'Mentoring'],
  },
  {
    id: 6,
    period: 'Jan 2016 — Mar 2017',
    company: 'Internet Brands, Inc.',
    role: 'QA Engineer Intern',
    description:
      'Cut post-release defects 10% via smoke & regression testing; documented processes in Confluence.',
    tags: ['Manual Testing', 'Regression', 'Confluence', 'Smoke Testing'],
  },
]

function ExperienceCard({ exp }) {
  return (
    <div className="card p-6">
      <div className="flex items-center gap-2 mb-3">
        <i className="ri-calendar-line text-accent text-sm"></i>
        <span className="font-mono text-xs text-accent">{exp.period}</span>
      </div>
      <h3 className="text-title font-bold text-[1rem] mb-0.5">{exp.company}</h3>
      <p className="text-muted text-sm mb-4">{exp.role}</p>
      <p className="text-muted text-sm leading-relaxed mb-5">{exp.description}</p>
      <div className="flex flex-wrap gap-2">
        {exp.tags.map(tag => (
          <span key={tag} className="tag">{tag}</span>
        ))}
      </div>
    </div>
  )
}

export default function Experience() {
  return (
    <div className="py-24">
      <div className="text-center mb-16">
        <p className="section-label">{"// experience"}</p>
        <h2 className="text-[1.5rem] sm:text-[1.75rem] font-bold text-title mb-2">Where I've Worked</h2>
        <p className="text-sm text-muted">From quality engineering to breaking things for a living.</p>
      </div>

      {/* ── Mobile: left-side line, single column ── */}
      <div className="md:hidden relative">
        <div className="absolute left-3.5 top-0 bottom-0 w-px bg-card-border" />
        <div className="flex flex-col gap-8">
          {experiences.map(exp => (
            <div key={exp.id} className="relative pl-10">
              <div className="absolute left-0 top-6 w-3.5 h-3.5 rounded-full bg-accent border-2 border-card-bg shadow-sm" />
              <ExperienceCard exp={exp} />
            </div>
          ))}
        </div>
      </div>

      {/* ── Desktop: alternating left/right ── */}
      <div className="hidden md:block relative">
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-card-border -translate-x-1/2" />
        <div className="flex flex-col gap-12">
          {experiences.map((exp, index) => {
            const isLeft = index % 2 === 0
            return (
              <div key={exp.id} className="relative grid grid-cols-2">
                <div className="absolute left-1/2 top-8 -translate-x-1/2 z-10
                                w-3.5 h-3.5 rounded-full bg-accent border-2 border-card-bg shadow-sm" />
                {isLeft ? (
                  <>
                    <div className="pr-10"><ExperienceCard exp={exp} /></div>
                    <div />
                  </>
                ) : (
                  <>
                    <div />
                    <div className="pl-10"><ExperienceCard exp={exp} /></div>
                  </>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* ── Resume button ── */}
      <div className="text-center mt-16">
        <a
          href={resume}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-card-border
                     bg-card-bg text-title text-sm font-medium hover:border-accent hover:text-accent
                     transition-colors shadow-sm"
        >
          Resume
          <i className="ri-download-line text-base"></i>
        </a>
      </div>

    </div>
  )
}
